<template>
  <div ref="contentOrgEl" class="content-organization">
    <!-- plus free access callout -->
    <plus-free-access-callout
      v-if="userAuthStore.plusFreeAccess.enabled"
      tour-id="plusFreeAccessCustomWorkouts"
    />

    <search-header
      title="My Custom Workouts"
      active="filters"
      filter-url="/my/custom-workouts"
      :show-tags="true"
      @tag-update="tagUpdate"
      @clear-all="clearAll"
    />

    <div ref="contentResultsEl" class="content-type-options">
      <search-filters
        :loading="page.loading"
        :show-keywords="true"
        :show-tags="true"
        :entity-type="entityType"
        @filter-update="filterUpdate"
      >
        <custom-workout-filters @filter-update="filterUpdate" />
      </search-filters>

      <!-- no expander currently used-->

      <div class="content-results">
        <!-- error state, modal will also show -->
        <callout v-if="errorMsg" type="error">
          <div>{{ errorMsg }}</div>
        </callout>

        <!-- result top bar -->
        <div class="results-details">
          <search-sort :total="searchResults.total" @sort-update="sortUpdate" />
          <search-result-actions :accessibility-expander-id="accessibilityExpanderId">
            <!-- filter collection specific actions -->
            <div class="action" data-tour="plus-free-access-add">
              <a
                class="btn -material add"
                href="/my/custom-workouts/create"
                aria-label="Add a Workout"
              >
                <i class="material-symbols-outlined btn-ico" aria-hidden="true">add</i>
                <span class="btn-txt -reduce">Add a Workout</span>
              </a>
            </div>
          </search-result-actions>
        </div>

        <!-- results -->
        <search-result-cards
          v-if="!errorMsg && (searchResults.loading || searchResults.total > 0)"
          v-slot="{ cardOrder }"
          :loading="searchResults.loading"
          :per-page-total="searchResults.perPage"
          :current-page-total="searchResultsData.length"
          :entity-type="entityType"
        >
          <custom-workout-card
            v-for="(customWorkout, index) in searchResultsData"
            :key="customWorkout.id"
            :custom-workout="customWorkout"
            :style="{ order: cardOrder(index) }"
          />
        </search-result-cards>
        <search-no-results v-else @clear-all="clearAll" />

        <!-- pagination -->
        <pagination
          :current-page="searchResults.currentPage"
          :total-pages="searchResults.totalPages"
          :total-items="searchResults.total"
          :per-page="searchResults.perPage"
          @page-update="pageUpdate"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import Pagination from '../search/pagination.vue'
import SearchHeader from '@/components/search/search-header.vue'
import SearchFilters from '@/components/search/search-filters.vue'
import SearchSort from '@/components/search/search-sort.vue'
import SearchResultActions from '@/components/search/search-result-actions.vue'
import { useUserAuthStore } from '@/stores/user-auth'
import { useSearchStore } from '@/stores/search'
import type { CustomWorkoutCardType } from '@/types/model-types'
import { removeArraySyntax } from '@/utils/helpers'
import { fbApi, isAbortError, objToSearchParams } from '@/utils/http-api'
import type {
  InitCheckboxFilter,
  RangeFilter,
  SortOption,
  CustomWorkoutFacets,
  CustomWorkoutPagination,
  DropdownFilter,
} from '@/types/search-types'
import Callout from '@/components/notifications/callout.vue'
import CustomWorkoutFilters from '@/components/custom-workouts/custom-workout-filters.vue'
import SearchResultCards from '@/components/search/search-result-cards.vue'
import CustomWorkoutCard from '@/components/custom-workouts/custom-workout-card.vue'
import SearchNoResults from '@/components/search/search-no-results.vue'
import { useSearchRoute } from '@/composables/search-route'
import PlusFreeAccessCallout from '@/components/notifications/plus-free-access-callout.vue'

/** TYPES **/
// noop

/** STATE **/
const route = useRoute()
const userAuthStore = useUserAuthStore()
const searchStore = useSearchStore()
const {
  contentResultsEl,
  contentOrgEl,
  tagUpdate,
  filterUpdate,
  sortUpdate,
  clearAll,
  pageUpdate,
} = useSearchRoute()

const page = ref<{
  loading: boolean
  error: string
}>({
  loading: false,
  error: '',
})

const searchResults = ref<{
  loading: boolean
  error: string
  currentPage: number
  totalPages: number
  total: number
  perPage: number
}>({
  loading: false,
  error: '',
  currentPage: 0,
  totalPages: 0,
  perPage: 20,
  total: 0,
})

const searchResultsData = shallowRef<CustomWorkoutCardType[]>([])

/** NON REACTIVE **/
let fetchAbort: AbortController | null = null

const accessibilityExpanderId = 'results-expander-control'
const entityType = 'custom_workout'

const checkboxFilters: Record<string, InitCheckboxFilter> = {
  difficulty: {
    label: 'Difficulty',
    name: 'difficulty',
    items: [
      { label: 'Level 1', value: 1 },
      { label: 'Level 2', value: 2 },
      { label: 'Level 3', value: 3 },
      { label: 'Level 4', value: 4 },
      { label: 'Level 5', value: 5 },
    ],
  },
  bodyfocus_ids: {
    label: 'Body Focus',
    name: 'focus',
    items: [
      { label: 'Upper', value: 1 },
      { label: 'Core', value: 2 },
      { label: 'Lower', value: 3 },
      { label: 'Total', value: 4 },
    ],
  },
  trainingtype_ids: {
    label: 'Training Type',
    name: 'trainingtype',
    items: [
      { label: 'Balance/Agility', value: 5 },
      { label: 'Barre', value: 6 },
      { label: 'Cardiovascular', value: 7 },
      { label: 'HIIT', value: 8 },
      { label: 'Kettlebell', value: 9 },
      { label: 'Kickboxing', value: 82 },
      { label: 'Low Impact', value: 10 },
      { label: 'Mobility', value: 96 },
      { label: 'Pilates', value: 11 },
      { label: 'Plyometric', value: 12 },
      { label: 'Pre & Postnatal', value: 95 },
      { label: 'Strength Training', value: 13 },
      { label: 'Stretching/Flexibility', value: 16 },
      { label: 'Toning', value: 14 },
      { label: 'Warm Up/Cool Down', value: 15 },
      { label: 'Yoga', value: 94 },
      { label: 'Other', value: 32 },
    ],
  },
}

const dropdownFilters: Record<string, DropdownFilter> = {
  history: {
    label: 'Completion',
    name: 'history',
    multiple: false,
    value: '',
    items: [
      { label: 'Complete', value: 'complete' },
      { label: 'Not Complete', value: 'not_complete' },
    ],
  },

  tags: {
    label: 'Tags',
    name: 'tags',
    multiple: true,
    value: [],
  },
}

const rangeFilters: Record<string, RangeFilter> = {
  duration: {
    name: 'duration',
    label: 'Minutes',
    units: 'Minutes',
    min: { name: 'minlength', value: '' },
    max: { name: 'maxlength', value: '' },
  },
}

const sortOptions: SortOption[] = [
  { label: 'Newest First', value: 'newest' },
  { label: 'Shortest', value: 'shortest' },
  { label: 'Longest', value: 'longest' },
  { label: 'Oldest', value: 'oldest' },
  { label: 'Relevance', value: 'relevance' },
]

/** COMPUTED **/
const errorMsg = computed(() => page.value.error || searchResults.value.error)

/** WATCHERS **/
watch(
  () => route.query,
  () => {
    // resync on every query string change
    syncUrlToState({ init: false })

    // refresh data
    fetchCustomWorkouts()
  },
)

/** LIFECYCLE **/
onBeforeMount(async () => {
  // clear everything
  searchStore.$reset()

  searchStore.setSortOptions(sortOptions)

  syncUrlToState({ init: true })

  // do not wait for this to finish before moving on
  fetchCustomWorkouts()
})

// cancel fetch request, especially if going to browse view as it will
// as it will use the search store
onBeforeUnmount(() => {
  if (fetchAbort) {
    fetchAbort.abort()
  }
})

/** METHODS **/

/**
 * Update pinia based on url query params as the source of truth.
 * Clear out values and reset them based on the query params
 */
function syncUrlToState({ init }: { init: boolean }) {
  // removes [] and forces array for items with [] in var names
  const queryParams = removeArraySyntax(route.query)

  // filters
  searchStore.setCheckboxFilters({
    checkboxFilters,
    queryStringFilters: queryParams,
    checkboxUrlParameterNames: searchStore.createIncludeExcludeParamMap(
      {
        difficulty: 'difficulty',
        focus: 'bodyfocus_ids',
        trainingtype: 'trainingtype_ids',
      },
      userAuthStore.features.VIDEO_FILTERS,
    ),
    init,
  })

  searchStore.setDropdownFilters({
    dropdownFilters,
    queryStringFilters: queryParams,
    dropdownUrlParameterNames: {
      tags: 'tags',
      history: 'history',
    },
    init,
  })

  searchStore.setRangeFilters({
    rangeFilters,
    queryStringFilters: queryParams,
    rangeUrlParameterNames: {
      minlength: { filterName: 'duration', field: 'min' },
      maxlength: { filterName: 'duration', field: 'max' },
    },
    init,
  })

  searchStore.setKeywordsAndSort(queryParams.keywords || '', queryParams.sort || 'newest')

  // rebuild tags last, after all pinia state is loaded
  searchStore.setInitialTags()
}

async function fetchCustomWorkouts() {
  // clear any error
  searchResults.value.error = ''
  searchResults.value.loading = true

  // format GET query params
  let params = route.query || {}
  params = removeArraySyntax(params)

  // reset pagination on a filter change
  if (!('page' in params)) {
    searchResults.value.currentPage = 1
  }

  try {
    // cancel any previous fetch
    if (fetchAbort) {
      fetchAbort.abort()
    }

    // re-init abort controller
    fetchAbort = new AbortController()

    const res = await fbApi.get('/custom-workouts', {
      searchParams: objToSearchParams(params),
      signal: fetchAbort.signal,
    })

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch custom workouts, please refresh the page and try again`)
    }

    const {
      paginatedCustomWorkouts,
      facets,
    }: {
      paginatedCustomWorkouts: CustomWorkoutPagination
      facets: CustomWorkoutFacets
    } = res.body.data

    searchResults.value.total = paginatedCustomWorkouts.total
    searchResults.value.currentPage = paginatedCustomWorkouts.current_page
    searchResults.value.totalPages = paginatedCustomWorkouts.last_page
    searchResultsData.value = paginatedCustomWorkouts.data

    // pinia facets
    searchStore.updateFacets(facets)
  } catch (err) {
    // skip if Aborted
    if (isAbortError(err)) {
      console.warn('fetchCustomWorkouts aborted')
      return
    }

    // oops model will show
    console.error(err)
    searchResults.value.error = `Unable to fetch custom workouts, please refresh the page and try again`
  } finally {
    searchResults.value.loading = false
  }
}
</script>
